import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { checkoutLoan } from '../../api/loans';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import type { AssetLoan } from '../../types/api';

export function CheckoutLoanModal({
  loan,
  open,
  onClose,
}: {
  loan: AssetLoan;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const itemName = loan.asset.orderLine?.customerCatalogItem?.name ?? 'this item';
  const requesterName = loan.requestedBy.firstName || loan.requestedBy.email;

  const mutation = useMutation({
    mutationFn: () => checkoutLoan(loan.id, notes.trim() || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['loan', loan.id] });
      queryClient.invalidateQueries({ queryKey: ['asset', loan.assetId] });
      showToast(`"${itemName}" checked out to ${requesterName}.`);
      setNotes('');
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not check out this loan.')),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Check out equipment"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button isLoading={mutation.isPending} onClick={() => mutation.mutate()}>
            Confirm check-out
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        <p className="text-sm text-slate-600">
          Hand <span className="font-medium text-slate-900">{itemName}</span> to {requesterName}. Due back{' '}
          {new Date(loan.dueDate).toLocaleDateString()}.
        </p>
        <Input
          label="Notes (optional)"
          placeholder="e.g. condition at hand-over"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
    </Modal>
  );
}
